import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  IconButton,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Spinner,
  Text,
  useDisclosure
} from '@chakra-ui/react';
import { FaArrowLeft, FaArrowRight, FaChevronDown, FaSearch, FaTimes } from 'react-icons/fa';
import { LABEL, MESSAGE } from '../../../../constants';
import { getAllPointOfWorks } from '../../api/getNomenclatures';

const ModifiedPaginatedDropdown = ({
  value,
  onChange,
  fetchData,
  label = LABEL.GENERIC_DROPDOWN.label,
  placeholder = LABEL.GENERIC_DROPDOWN.placeholder,
  size = 10,
  cifValue
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');

  const [selectedItem, setSelectedItem] = useState(null);

  const selectedLabel = selectedItem
    ? `${selectedItem.code} - ${selectedItem.name}`
    : placeholder;

  useEffect(() => {
    const fetchSelected = async () => {
      if (value) {
        setIsLoading(true);
        try {
          const response = await fetchData({
            cif: cifValue,
            searchTerm: value,
            pageNumber: 0,
            size: 1,
            orderByList: [{ field: 'name', direction: 'ASCENDING' }]
          });
          if (response && response.content && response.content.length > 0) {
            const item = response.content[0];
            setSelectedItem(item);
          } else {
            setSelectedItem(null);
            onChange(null);
          }
        } catch (error) {
          console.error(MESSAGE.POINT_OF_WORKS.error, error);
          setSelectedItem(null);
          onChange(null);
        } finally {
          setIsLoading(false);
        }
      } else {
        setSelectedItem(null);
      }
    };
    fetchSelected();
  }, [value, cifValue, fetchData, onChange]);

  const loadItems = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await fetchData({
        cif: cifValue,
        searchTerm,
        pageNumber: currentPage,
        size,
        orderByList: [{ field: 'name', direction: 'ASCENDING' }]
      });
      if (response) {
        const totalItems = response.total;
        const fetchedItems = response.content || [];
        setItems(fetchedItems);
        setTotalPages(Math.ceil(totalItems / size));
      }
    } catch (error) {
      console.error(MESSAGE.POINT_OF_WORKS.error, error);
    } finally {
      setIsLoading(false);
    }
  }, [fetchData, currentPage, size, cifValue, searchTerm]);

  useEffect(() => {
    if (isOpen) {
      loadItems();
    }
  }, [isOpen, loadItems]);

  const handleSelectItem = (item) => {
    setSelectedItem(item);
    onChange(item.code);
    onClose();
  };

  const handleClearSelection = (event) => {
    event.stopPropagation();
    setSelectedItem(null);
    onChange(null);
  };

  const handlePrevious = () => {
    if (currentPage > 0) {
      setCurrentPage((previous) => previous - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages - 1) {
      setCurrentPage((previous) => previous + 1);
    }
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setCurrentPage(0);
  };

  return (
    <Menu isOpen={isOpen} onOpen={onOpen} onClose={onClose} closeOnSelect={false}>
      <Flex align="center" width="100%">
        <MenuButton
          as={Button}
          variant="outline"
          textColor="black"
          fontSize="sm"
          size="sm"
          width="100%"
          textAlign="left"
          fontWeight="light"
          rightIcon={<FaChevronDown />}
        >
          {selectedLabel}
          {isLoading && <Spinner size="xs" ml="2" />}
        </MenuButton>
        {value && (
          <IconButton
            icon={<FaTimes />}
            size="sm"
            ml={2}
            aria-label={LABEL.CLEAR_SELECTION.label}
            onClick={handleClearSelection}
          />
        )}
      </Flex>

      <MenuList width="600px" p="2">
        <Box display="flex" alignItems="center" mb="2">
          <FaSearch style={{ marginRight: '6px', color: '#718096' }} />
          <Input
            placeholder={LABEL.SEARCH.label}
            size="sm"
            value={searchTerm}
            onChange={handleSearchChange}
          />
        </Box>

        {isLoading ? (
          <Box textAlign="center" p="2" width="600px" height="400px">
            <Spinner size="sm" />
          </Box>
        ) : (
          <>
            {items.length === 0 ? (
              <Box textAlign="center" p="2">
                <Text fontSize="sm" color="gray.500">
                  {LABEL.EMPTY_FILTER.label}
                </Text>
              </Box>
            ) : (
              items.map((item) => (
                <MenuItem key={item.id} onClick={() => handleSelectItem(item)} fontSize="sm">
                  {item.code} - {item.name}
                </MenuItem>
              ))
            )}
          </>
        )}

        <Flex
          mt="2"
          justify="space-between"
          align="center"
          px="2"
          visibility={isLoading ? 'hidden' : 'visible'}
        >
          <IconButton
            icon={<FaArrowLeft />}
            size="xs"
            aria-label={LABEL.PREVIOUS_PAGE.label}
            onClick={handlePrevious}
            isDisabled={currentPage <= 0}
          />
          <Text fontSize="xs">
            Page {currentPage + 1} of {Math.max(totalPages, 1)}
          </Text>
          <IconButton
            icon={<FaArrowRight />}
            size="xs"
            aria-label={LABEL.NEXT_PAGE.label}
            onClick={handleNext}
            isDisabled={currentPage >= totalPages - 1 || totalPages === 0}
          />
        </Flex>
      </MenuList>
    </Menu>
  );
};

const PointOfWorksDropdown = ({ value, onChange, cifValue, ...props }) => {
  return (
    <ModifiedPaginatedDropdown
      value={value} 
      onChange={onChange}
      fetchData={getAllPointOfWorks}
      placeholder={LABEL.POW_DROPDOWN.placeholder}
      label={LABEL.POW_DROPDOWN.label}
      cifValue={cifValue}
      {...props}
    />
  );
};

export default PointOfWorksDropdown;
